import { ALV_NOLLAAN_LUKITUT_LAJIT, Lasku, LaskunAlv, LaskunLiitetiedosto, LaskunTyyppi } from '@lemon/core-firebase/model/lasku'
import { NumberDate } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'

import type { AlvinLukituksenSelite } from './lasku-rivi.component'

export const LIITTEEN_TUETUT_MUODOT: readonly string[] = ['jpeg', 'jpg', 'png', 'webp', 'tiff', 'gif', 'svg', 'pdf', 'heic', 'heif']
export const LIITTEEN_ENIMMAISKOKO = 5 * 1024 * 1024
export const LIITTEIDEN_ENIMMAISYHTEISKOKO = 8 * 1024 * 1024
export const LIITTEIDEN_ENIMMAISMAARA = 8
export const YKSIKON_ALKUKIRJAINTEN_VALI_MILLIS = 600

const ALV_KANNAN_ALKAMISEN_ENNAKKO_PAIVIA = 14
const ALV_KANNAN_PAATTYMISEN_ENNAKKO_PAIVIA = 90

const _dateService = new DateService()

export interface LisattavaLiite {
  paate: string
  koko: number
}

export type LiitteidenRajavirhe = { laji: 'vaara-muoto' } | { laji: 'liian-suuri'; kokoMax: number; kokoNyt: number } | { laji: 'liian-monta'; lukumaaraMax: number; lukumaaraNyt: number }

export interface LiitteenLataus extends LaskunLiitetiedosto {
  uploadDone?: boolean
  uploadError?: string
}

export type LiitteenLatauksenTila = 'puuttuu' | 'epaonnistui' | 'kesken' | 'valmis'

export interface YksikonAlkukirjaimet {
  alkukirjaimet: string
  aikaleima: number
}

/** Rivien alv-kannat, jotka ovat jo laskulla tai sen liittyvillä laskuilla. Ne tarjotaan aina, vaikka kanta olisi päättynyt. */
export function annaLaskullaKaytetytAlvTunnisteet(juurilasku: Pick<Lasku, 'tuotteet' | 'liittyvatLaskut'> | null | undefined): Set<string> {
  const tunnisteet = new Set<string>()
  const laskut = juurilasku ? [juurilasku, ...Object.values(juurilasku.liittyvatLaskut ?? {})] : []
  for (const lasku of laskut) {
    for (const tuote of lasku.tuotteet ?? []) {
      if (tuote?.alv?.tunniste) {
        tunnisteet.add(tuote.alv.tunniste)
      }
    }
  }
  return tunnisteet
}

/**
 * Uudelle riville tarjotaan kanta, joka alkaa viimeistään kahden viikon päästä eikä pääty seuraavan kolmen kuukauden
 * aikana. Näin kannan vaihtuessa uutta voi käyttää hieman etukäteen, eikä juuri päättyvää enää tarjota.
 */
export function annaTarjottavatAlvKannat(alvt: LaskunAlv[], laskullaKaytetyt: ReadonlySet<string>, tanaan: NumberDate): LaskunAlv[] {
  const alkaaViimeistaan = _dateService.lisaaPaiviaNumber(tanaan, ALV_KANNAN_ALKAMISEN_ENNAKKO_PAIVIA)
  const voimassaVahintaan = _dateService.lisaaPaiviaNumber(tanaan, ALV_KANNAN_PAATTYMISEN_ENNAKKO_PAIVIA)
  return alvt.filter(alv => {
    if (laskullaKaytetyt.has(alv.tunniste)) {
      return true
    }
    const onAlkanut = !alv.s || alv.s <= alkaaViimeistaan
    const onPaattynyt = !!alv.e && alv.e < voimassaVahintaan
    return onAlkanut && !onPaattynyt
  })
}

/** Alv-rekisteriin kuulumaton laskuttaja käyttää vain nollakantaa, mutta hyvitys ja korjaus noudattavat alkuperäistä laskua. */
export function onkoAlvRajattuNollaan(laskuttajaAlvRekisterissa: boolean, onkoHyvitysTaiKorjaus: boolean): boolean {
  return !laskuttajaAlvRekisterissa && !onkoHyvitysTaiKorjaus
}

export function onkoAlvKenttaMuokattavissa(tyyppi: LaskunTyyppi | null | undefined, alvRajattuNollaan: boolean): boolean {
  if (alvRajattuNollaan || !tyyppi) {
    return false
  }
  return !ALV_NOLLAAN_LUKITUT_LAJIT.includes(tyyppi.avain)
}

export function annaAlvinLukituksenSelite(tyyppi: LaskunTyyppi, alvRajattuNollaan: boolean): AlvinLukituksenSelite | null {
  if (alvRajattuNollaan) {
    return { kaannosavain: 'lasku.alv-lukittu-ei-alv-rekisterissa', myynninLaji: tyyppi.avain }
  }
  if (!onkoAlvKenttaMuokattavissa(tyyppi, alvRajattuNollaan)) {
    return { kaannosavain: 'lasku.alv-lukittu-kaannetty-verovelvollisuus', myynninLaji: tyyppi.avain }
  }
  return null
}

/** Lisättävien tiedostojen muoto ja koko sekä laskun liitteiden yhteiskoko ja määrä yhdessä jo lisättyjen kanssa. */
export function tarkistaLiitteidenRajat(lisattavat: LisattavaLiite[], olemassaolevat: Pick<LaskunLiitetiedosto, 'koko'>[]): LiitteidenRajavirhe | null {
  let kokonaiskoko = olemassaolevat.reduce((summa, liite) => summa + liite.koko, 0)
  let lukumaara = olemassaolevat.length
  for (const liite of lisattavat) {
    kokonaiskoko += liite.koko
    lukumaara++
    if (!liite.paate || !LIITTEEN_TUETUT_MUODOT.includes(liite.paate.toLowerCase())) {
      return { laji: 'vaara-muoto' }
    }
    if (liite.koko > LIITTEEN_ENIMMAISKOKO) {
      return { laji: 'liian-suuri', kokoMax: LIITTEEN_ENIMMAISKOKO, kokoNyt: liite.koko }
    }
  }
  if (kokonaiskoko > LIITTEIDEN_ENIMMAISYHTEISKOKO) {
    return { laji: 'liian-suuri', kokoMax: LIITTEIDEN_ENIMMAISYHTEISKOKO, kokoNyt: kokonaiskoko }
  }
  if (lukumaara > LIITTEIDEN_ENIMMAISMAARA) {
    return { laji: 'liian-monta', lukumaaraMax: LIITTEIDEN_ENIMMAISMAARA, lukumaaraNyt: lukumaara }
  }
  return null
}

/** Laskua ei voi lähettää, ennen kuin jokainen liite on ladattu onnistuneesti. */
export function annaLiitteenLatauksenTila(liite: LiitteenLataus | null | undefined): LiitteenLatauksenTila {
  if (!liite) {
    return 'puuttuu'
  }
  if (liite.uploadError) {
    return 'epaonnistui'
  }
  if (!liite.uploadDone) {
    return 'kesken'
  }
  return 'valmis'
}

/** Määräkenttään kirjoitetut kirjaimet kootaan yksikön hakusanaksi, kunnes kirjainten väli venyy yli 600 ms:iin. */
export function kokoaYksikonAlkukirjaimet(edelliset: YksikonAlkukirjaimet, kirjain: string, nyt: number): YksikonAlkukirjaimet {
  const jatkaaAlkukirjaimia = nyt - edelliset.aikaleima < YKSIKON_ALKUKIRJAINTEN_VALI_MILLIS
  return {
    alkukirjaimet: (jatkaaAlkukirjaimia ? edelliset.alkukirjaimet : '') + kirjain.toLowerCase(),
    aikaleima: nyt,
  }
}
